import React from 'react';
import { BarChartHorizontal } from 'lucide-react';
import { ChartColumn } from 'lucide-react';
import { PieChart } from 'lucide-react';
import { Grid3x3 } from 'lucide-react';
import { TrendingUp } from 'lucide-react';
import { CustomSelectOption } from 'apps/sigma2-ui/src/components/ui/CustomSelect';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';
import { ViewMode } from 'libs/end-to-end/src/view-configurations';
import { TableViewMode } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/TableViewMode';
import { PieChartGridMode } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/PieChartGridMode';
import { LineChartMode } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/LineChartMode';
import { BarChartMode } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/BarChartMode';
import { TableViewFilterProps } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { TableViewModeArguments } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { TableViewColorFilter } from 'apps/sigma2-ui/src/explore-section/components/ViewConfiguration/TableColorFilter';
import { TableViewMetricFilter } from 'apps/sigma2-ui/src/explore-section/components/ViewConfiguration/MetricFilter';
import { GroupedBarChartMode } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/GroupedBarChartMode';
import { HorizontalBarChartMode } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/HorizontalBarChartMode';
import { StackedBarChartMode } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/StackedBarChartMode';
import { IconStackedBarChartSvg } from 'apps/sigma2-ui/src/components/svg/IconStackedBarChart';
import { HeatmapViewMode } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/HeatmapViewMode';
import { FilterBreakdownSelector } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/FilterBreakdownSelector';
import { OverallDisplayModeFilter } from 'apps/sigma2-ui/src/components/OverallDisplayModeFilter';

const clearSelectedMetric = (viewConfiguration: ViewConfigurationSchema) => {
  return {
    ...viewConfiguration,
    selectedMetric: undefined,
  };
};
export interface TableViewModelOption extends CustomSelectOption {
  component: React.ComponentType<TableViewModeArguments>;
  filters: {
    id: string;
    component: React.ComponentType<TableViewFilterProps>;
  }[];
  updateViewConfiguration?: (
    viewConfiguration: ViewConfigurationSchema,
  ) => ViewConfigurationSchema;
}

const FILTER_IDS = {
  COLOR_FILTER: 'color-filter',
  METRIC_FILTER: 'metric-filter',
  FILTER_BREAKDOWN: 'filter-breakdown',
  OVERALL_DISPLAY_FILTER: 'overall-display-filter',
};

/**
 * Configuration for view mode options in the TableView selector
 */
export const viewModeOptions: TableViewModelOption[] = [
  {
    value: 'table' as const,
    label: 'Table',
    icon: React.createElement(Grid3x3, { size: 16 }),
    component: TableViewMode,
    filters: [
      { id: FILTER_IDS.COLOR_FILTER, component: TableViewColorFilter },
      {
        id: FILTER_IDS.OVERALL_DISPLAY_FILTER,
        component: OverallDisplayModeFilter,
      },
    ],
    updateViewConfiguration: clearSelectedMetric,
  },
  {
    value: 'bar-chart' as const,
    label: 'Bar Chart',
    icon: React.createElement(ChartColumn, { size: 16 }),
    component: BarChartMode,
    filters: [
      { id: FILTER_IDS.METRIC_FILTER, component: TableViewMetricFilter },
      {
        id: FILTER_IDS.OVERALL_DISPLAY_FILTER,
        component: OverallDisplayModeFilter,
      },
    ],
  },
  {
    value: 'pie-grid-chart' as const,
    label: 'Pie Chart Grid',
    icon: React.createElement(PieChart, { size: 16 }),
    component: PieChartGridMode,
    filters: [
      {
        id: FILTER_IDS.OVERALL_DISPLAY_FILTER,
        component: OverallDisplayModeFilter,
      },
    ],
    updateViewConfiguration: clearSelectedMetric,
  },
  {
    value: 'line-chart' as const,
    label: 'Line Chart',
    icon: React.createElement(TrendingUp, { size: 16 }),
    component: LineChartMode,
    filters: [
      { id: FILTER_IDS.METRIC_FILTER, component: TableViewMetricFilter },
      {
        id: FILTER_IDS.OVERALL_DISPLAY_FILTER,
        component: OverallDisplayModeFilter,
      },
    ],
  },
  {
    value: 'grouped-bar-chart' as const,
    label: 'Grouped Bar Chart',
    icon: React.createElement(BarChartHorizontal, { size: 16 }),
    component: GroupedBarChartMode,
    filters: [
      {
        id: FILTER_IDS.OVERALL_DISPLAY_FILTER,
        component: OverallDisplayModeFilter,
      },
    ],
    updateViewConfiguration: clearSelectedMetric,
  },
  {
    value: 'horizontal-bar-chart' as const,
    label: 'Horizontal Bar Chart',
    icon: React.createElement(BarChartHorizontal, { size: 16 }),
    component: HorizontalBarChartMode,
    filters: [
      { id: FILTER_IDS.METRIC_FILTER, component: TableViewMetricFilter },
      {
        id: FILTER_IDS.OVERALL_DISPLAY_FILTER,
        component: OverallDisplayModeFilter,
      },
    ],
  },
  {
    value: 'stacked-bar-chart' as const,
    label: 'Stacked Bar Chart',
    icon: React.createElement(IconStackedBarChartSvg, {
      svg: { className: 'w-4 h-4' },
    }),
    component: StackedBarChartMode,
    filters: [
      { id: 'overall-display-filter', component: OverallDisplayModeFilter },
    ],
    updateViewConfiguration: clearSelectedMetric,
  },
];

export const heatmapViewModeOptions: TableViewModelOption[] = [
  {
    value: 'heatmap',
    label: 'Heatmap',
    icon: React.createElement(Grid3x3, { size: 16 }),
    component: HeatmapViewMode,
    filters: [],
  },
  ...viewModeOptions.slice(1).map((option) => ({
    ...option,
    disabled: option.value === 'pie-grid-chart' ? true : option.disabled,
    filters: [
      ...option.filters.filter(
        (filter) => filter.id !== FILTER_IDS.OVERALL_DISPLAY_FILTER,
      ),
      {
        id: FILTER_IDS.FILTER_BREAKDOWN,
        component: FilterBreakdownSelector,
      },
    ],
  })),
];

/**
 * Default view mode for TableView component
 */
export const DEFAULT_VIEW_MODE: ViewMode = 'table';
export const HEATMAP_VIEW_MODE: ViewMode = 'heatmap';

/**
 * Height of the bar chart visualization
 */
export const BAR_CHART_HEIGHT = 400;
